import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MessageSquare, Phone, Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate, fmtDateTime, money, telLink, waLink, pingNotifications } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can, INTERACTION_TYPES } from "@/lib/rbac";
import { LeadBadge, PageHead, StatusBadge } from "@/components/Bits";
import { HandoffButton } from "@/components/HandoffButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { OutcomeSelect } from "@/components/OutcomeSelect";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [openQuote, setOpenQuote] = useState(false);
  const [quote, setQuote] = useState({ services: "", price: "", ppf_sqft: "" });
  const [estimate, setEstimate] = useState(null);
  const [openInt, setOpenInt] = useState(false);
  const [interaction, setInteraction] = useState({ type: "Call", notes: "" });
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const canQuote = can(user, "quotes:write");
  const canLog = can(user, "interactions:write");
  const canDelete = can(user, "customers:delete");

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Customer deleted");
      navigate("/customers");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setDeleting(false);
    }
  };

  const load = () =>
    api
      .get(`/customers/${id}`)
      .then(({ data }) => {
        setData(data);
        setErr(false);
      })
      .catch(() => setErr(true));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const [priceInfo, setPriceInfo] = useState(null);

  // Auto-populate PPF sqft from the car's model config the moment the quote dialog opens.
  useEffect(() => {
    const veh = data?.customer?.vehicle;
    if (openQuote && veh && (veh.make || veh.model)) {
      api
        .get("/pricing/auto", { params: { make: veh.make || "", model: veh.model || "" } })
        .then(({ data: d }) => {
          if (d?.sqft)
            setQuote((q) => (q.ppf_sqft === "" ? { ...q, ppf_sqft: String(d.sqft) } : q));
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openQuote]);

  const autoPrice = async (services, sqft) => {
    // Auto-fill the PPF price from the car's sqft + Standard/Premium brand list. Editable after.
    const film = (services || "").toLowerCase();
    if (!/glossy|gloss|matte|x250|x 250/.test(film)) {
      setPriceInfo(null);
      return;
    }
    try {
      const { data } = await api.get("/pricing/auto", {
        params: {
          make: customer?.vehicle?.make || "",
          model: customer?.vehicle?.model || "",
          service: services,
          ...(sqft === "" || sqft === undefined ? {} : { sqft: Number(sqft) }),
        },
      });
      if (data.price != null) {
        setQuote((q) => ({ ...q, price: String(data.price) }));
        setPriceInfo(data);
      } else {
        setPriceInfo(data);
      }
    } catch {
      setPriceInfo(null);
    }
  };

  const runEstimate = async (services, sqft) => {
    autoPrice(services, sqft);
    const list = services.split(",").map((x) => x.trim()).filter(Boolean);
    if (list.length === 0) {
      setEstimate(null);
      return;
    }
    try {
      const { data } = await api.post("/consumption/estimate", {
        studio_id: customer?.studio_id,
        services: list,
        brand: customer?.vehicle?.make,
        model: customer?.vehicle?.model,
        ...(sqft === "" || sqft === undefined ? {} : { ppf_sqft: Number(sqft) }),
      });
      setEstimate(data);
      if (sqft === "" || sqft === undefined)
        setQuote((q) => ({ ...q, ppf_sqft: String(data.ppf_sqft || "") }));
    } catch {
      setEstimate(null);
    }
  };

  const addQuote = async () => {
    try {
      await api.post(`/customers/${id}/quotes`, {
        services: quote.services.split(",").map((s) => s.trim()).filter(Boolean),
        price: Number(quote.price || 0),
        ...(quote.ppf_sqft === "" ? {} : { ppf_sqft: Number(quote.ppf_sqft) }),
      });
      toast.success("Quote created");
      setOpenQuote(false);
      setQuote({ services: "", price: "", ppf_sqft: "" });
      setEstimate(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const convert = async (q) => {
    try {
      await api.post(`/quotes/${q.id}/convert`);
      toast.success("Quote converted — job created and booked");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const addInteraction = async () => {
    try {
      await api.post(`/customers/${id}/interactions`, interaction);
      toast.success("Interaction logged");
      setOpenInt(false);
      setInteraction({ type: "Call", notes: "" });
      pingNotifications();
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (err)
    return (
      <div data-testid="customer-detail-error" className="py-16 text-center">
        <p className="text-sm text-slate-600">
          We couldn't load this customer. Please try again.
        </p>
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button variant="outline" onClick={() => { setErr(false); load(); }}>
            Retry
          </Button>
          <Link to="/customers" className="text-sm text-crimson-600 hover:underline">
            Back to customers
          </Link>
        </div>
      </div>
    );
  if (!data) return <p className="text-slate-500">Loading…</p>;
  const { customer, jobs = [], quotes = [], interactions = [] } = data;
  const v = customer.vehicle || {};
  const contact = async (channel, url) => {
    if (customer.phone && url) {
      if (channel === "Call") window.location.href = url;
      else window.open(url, "_blank");
    }
    if (!canLog) return;
    try {
      await api.post(`/customers/${id}/interactions`, { type: channel, notes: "", outcome: "" });
      toast.success(`${channel} logged — add the response below`);
      pingNotifications();
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };
  const revenue = jobs
    .filter((j) => ["Delivered", "Follow-up", "Repeat/Referral"].includes(j.status))
    .reduce((a, j) => a + j.price, 0);

  return (
    <div>
      <Link to="/customers" className="mb-4 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand-ink">
        <ArrowLeft className="h-4 w-4" /> Back to customers
      </Link>
      <PageHead title={customer.name} subtitle={`${customer.phone} · ${customer.email || "no email"}`}>
        {customer.phone && (
          <>
            <Button
              variant="outline"
              className="min-h-[44px]"
              data-testid="customer-call-btn"
              onClick={() => contact("Call", telLink(customer.phone))}
            >
              <Phone className="mr-2 h-4 w-4" /> Call
            </Button>
            <Button
              variant="outline"
              className="min-h-[44px] border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              data-testid="customer-whatsapp-btn"
              onClick={() => contact("WhatsApp", waLink(customer.phone))}
            >
              <MessageSquare className="mr-2 h-4 w-4" /> WhatsApp
            </Button>
          </>
        )}
        {canDelete && (
          <Button
            variant="outline"
            className="min-h-[44px] border-rose-200 text-rose-600 hover:bg-rose-50"
            data-testid="customer-delete-btn"
            onClick={() => setShowDelete(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" /> Delete
          </Button>
        )}
        <HandoffButton user={user} record={customer} kind="lead" onDone={load} />
      </PageHead>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="border-brand-line shadow-none">
          <CardHeader>
            <CardTitle className="text-lg">Vehicle</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm" data-testid="customer-vehicle-card">
            {[
              ["Make", v.make],
              ["Model", v.model],
              ["Year", v.year],
              ["Colour", v.colour],
              ["VIN", v.vin],
              ["Plate", v.plate],
            ].map(([k, val]) => (
              <div key={k} className="flex justify-between border-b border-slate-100 pb-1.5">
                <span className="text-slate-500">{k}</span>
                <span className="font-medium">{val || "—"}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-brand-line shadow-none">
          <CardHeader>
            <CardTitle className="text-lg">Acquisition</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div data-testid="customer-lead-source">
              <LeadBadge source={customer.lead_source} />
            </div>
            <div data-testid="customer-assigned-to">
              <span className="text-xs uppercase tracking-wide text-slate-500">Assigned to</span>
              <p className="font-medium text-brand-ink">{customer.assigned_to || "Unassigned"}</p>
              {customer.handed_off && (
                <p className="mt-1 text-xs text-slate-500" data-testid="customer-handoff-note">
                  Handed to Central Sales by {customer.handed_off_by || "studio"}
                  {customer.handed_off_at ? ` · ${fmtDate(customer.handed_off_at)}` : ""}
                </p>
              )}
            </div>
            <p className="text-slate-500">Customer since {fmtDate(customer.created_at)}</p>
          </CardContent>
        </Card>

        <Card className="border-brand-line shadow-none">
          <CardHeader>
            <CardTitle className="text-lg">Lifetime value</CardTitle>
          </CardHeader>
          <CardContent>
            <p data-testid="customer-lifetime-value" className="font-display text-3xl font-semibold">
              {money(revenue)}
            </p>
            <p className="mt-1 text-xs text-slate-500">{jobs.length} job(s) on record</p>
          </CardContent>
        </Card>
      </div>

      <h2 className="font-display mb-3 mt-8 text-lg font-semibold">Quotes</h2>
      <Card className="border-brand-line shadow-none">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base text-slate-500">
            {quotes.length} quote(s) on record
          </CardTitle>
          {canQuote && (
            <Dialog open={openQuote} onOpenChange={setOpenQuote}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="add-quote-btn">
                  <Receipt className="mr-2 h-3.5 w-3.5" /> New quote
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>New quote</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Services quoted (comma separated)</Label>
                    <Input
                      data-testid="quote-services-input"
                      value={quote.services}
                      onChange={(e) => setQuote((q) => ({ ...q, services: e.target.value }))}
                      onBlur={(e) => runEstimate(e.target.value, quote.ppf_sqft)}
                      placeholder="Full Body PPF, Ceramic Coating 9H"
                    />
                  </div>
                  <div>
                    <Label>PPF sqft for this car</Label>
                    <Input
                      inputMode="decimal"
                      data-testid="quote-ppf-sqft-input"
                      value={quote.ppf_sqft}
                      onChange={(e) => setQuote((q) => ({ ...q, ppf_sqft: e.target.value }))}
                      onBlur={(e) => runEstimate(quote.services, e.target.value)}
                    />
                    <p className="mt-1.5 text-xs text-slate-500">
                      Pre-filled from the {customer?.vehicle?.make} {customer?.vehicle?.model}{" "}
                      config — edit it for partial PPF or a custom cut.
                    </p>
                  </div>
                  {estimate && estimate.items.length > 0 && (
                    <div
                      className="rounded-md bg-slate-50 p-3 text-xs"
                      data-testid="quote-estimate-block"
                    >
                      <p className="font-semibold text-slate-700">Estimated material use</p>
                      {estimate.items.map((it) => (
                        <p key={it.name} className="mt-1 flex justify-between text-slate-600">
                          <span>
                            {it.name}
                            {it.from_ppf_sqft ? " (from PPF sqft)" : ""}
                          </span>
                          <span>
                            {it.quantity.toFixed(2)} {it.unit}
                            <span className="ml-1 text-slate-400">
                              of {it.in_stock.toFixed(2)} in stock
                            </span>
                          </span>
                        </p>
                      ))}
                    </div>
                  )}
                  <div>
                    <Label>Price quoted (₹)</Label>
                    <Input
                      data-testid="quote-price-input"
                      value={quote.price}
                      onChange={(e) => setQuote((q) => ({ ...q, price: e.target.value }))}
                    />
                    {priceInfo && priceInfo.price != null && (
                      <p className="mt-1.5 text-xs text-emerald-700" data-testid="quote-price-autofill-hint">
                        Auto-filled from the {priceInfo.premium ? "Premium" : "Standard"} price list
                        · {priceInfo.column} · {priceInfo.tier_sqft} sqft tier ({priceInfo.sqft} sqft).
                        Editable — override if needed.
                      </p>
                    )}
                    {priceInfo && priceInfo.price == null && (
                      <p className="mt-1.5 text-xs text-slate-500" data-testid="quote-price-autofill-hint">
                        No auto price ({priceInfo.reason || "check the car's sqft & service"}).
                      </p>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="save-quote-btn" onClick={addQuote} disabled={!quote.price}>
                    Save quote
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table data-testid="customer-quotes-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Services quoted</TableHead>
                <TableHead className="text-right">Price quoted</TableHead>
                <TableHead>Converted</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-slate-500">
                    No quotes yet.
                  </TableCell>
                </TableRow>
              )}
              {quotes.map((q) => (
                <TableRow key={q.id} className="hover:bg-brand-tint/60">
                  <TableCell>{fmtDate(q.date)}</TableCell>
                  <TableCell className="max-w-xs truncate">{(q.services || []).join(", ")}</TableCell>
                  <TableCell className="text-right">{money(q.price)}</TableCell>
                  <TableCell>
                    <Badge
                      data-testid={`quote-converted-${q.id}`}
                      className={`border-0 ${q.converted ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-700"}`}
                    >
                      {q.converted ? "Yes" : "No"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {canQuote && !q.converted && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`convert-quote-${q.id}`}
                        onClick={() => convert(q)}
                      >
                        Mark converted
                      </Button>
                    )}
                    {q.converted && q.job_id && (
                      <Link
                        to={`/jobs/${q.job_id}`}
                        data-testid={`quote-job-link-${q.id}`}
                        className="text-sm text-crimson-600 hover:underline"
                      >
                        View job
                      </Link>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <h2 className="font-display mb-3 mt-8 text-lg font-semibold">Interaction log</h2>
      <Card className="border-brand-line shadow-none">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base text-slate-500">
            {interactions.length} interaction(s), newest first
          </CardTitle>
          {canLog && (
            <Dialog open={openInt} onOpenChange={setOpenInt}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="add-interaction-btn">
                  <MessageSquare className="mr-2 h-3.5 w-3.5" /> Log interaction
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Log interaction</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Type</Label>
                    <Select
                      value={interaction.type}
                      onValueChange={(v) => setInteraction((i) => ({ ...i, type: v }))}
                    >
                      <SelectTrigger data-testid="interaction-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INTERACTION_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      data-testid="interaction-notes-input"
                      value={interaction.notes}
                      onChange={(e) => setInteraction((i) => ({ ...i, notes: e.target.value }))}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    data-testid="save-interaction-btn"
                    onClick={addInteraction}
                    disabled={!interaction.notes}
                  >
                    Save interaction
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent className="space-y-3" data-testid="interaction-log">
          {interactions.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-500">No interactions logged yet.</p>
          )}
          {interactions.map((i) => (
            <div key={i.id} className="rounded-md border border-brand-line p-3">
              <div className="flex items-center justify-between gap-3">
                <Badge className="border-0 bg-crimson-50 text-crimson-700">{i.type}</Badge>
                <span className="text-xs text-slate-500">
                  {fmtDateTime(i.timestamp)} · {i.staff_name || "—"}
                </span>
              </div>
              {i.notes && <p className="mt-2 text-sm text-slate-700">{i.notes}</p>}
              {i.outcome ? (
                <p className="mt-2 text-sm text-brand-ink">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Response: </span>
                  {i.outcome}
                </p>
              ) : (
                canLog && (
                  <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2" data-testid={`pending-response-${i.id}`}>
                    <p className="mb-1 text-xs font-medium text-amber-700">Response pending — add the outcome:</p>
                    <OutcomeSelect
                      patchUrl={`/customers/${id}/interactions/${i.id}`}
                      testid={`cust-outcome-${i.id}`}
                      onSaved={load}
                    />
                  </div>
                )
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <h2 className="font-display mb-3 mt-8 text-lg font-semibold">Job history</h2>
      <Card className="border-brand-line shadow-none">
        <CardContent className="overflow-x-auto p-0">
          <Table data-testid="customer-jobs-table">
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Services</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Completed</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right">Profit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-slate-500">
                    No jobs yet.
                  </TableCell>
                </TableRow>
              )}
              {jobs.map((j) => (
                <TableRow key={j.id} className="hover:bg-brand-tint/60">
                  <TableCell>{fmtDate(j.created_at)}</TableCell>
                  <TableCell className="max-w-xs truncate">
                    <Link to={`/jobs/${j.id}`} className="hover:text-crimson-600" data-testid={`cust-job-link-${j.id}`}>
                      {(j.services || []).join(", ") || "—"}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={j.status} />
                  </TableCell>
                  <TableCell>{fmtDate(j.completed_at)}</TableCell>
                  <TableCell className="text-right">{money(j.price)}</TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600">
                    {money(j.price - (j.material_cost || 0))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDelete} onOpenChange={(o) => !o && setShowDelete(false)}>
        <DialogContent className="sm:max-w-md" data-testid="customer-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete customer?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This permanently deletes <strong>{customer.name}</strong> along with their quotes and
            interaction history. Customers that still have jobs can't be deleted — reassign or
            delete those jobs first. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDelete(false)}>
              Cancel
            </Button>
            <Button
              className="bg-rose-600 text-white hover:bg-rose-700"
              data-testid="confirm-customer-delete-btn"
              disabled={deleting}
              onClick={doDelete}
            >
              {deleting ? "Deleting…" : "Delete customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
